import React, { useEffect } from 'react';
import ChoicePicker from '../components/ChoicePicker';
import TextInput from '../components/TextInput';
import WheelDatePicker, { type WheelDateValue } from '../components/WheelDatePicker';
import { sendIcon } from '../chatIcons';
import type { useHireHealthSurveyFlow } from './useHireHealthSurveyFlow';
import type { FitChoice, YesNo } from './hireHealthSurveyTypes';
import { stepQuestionKey } from './hireHealthSurveySteps';
import {
  HIRE_HEALTH_DETAIL_MAX_CHARS,
  HIRE_HEALTH_TEXT_BOX_MAX_CHARS,
} from './hireHealthSurveyTextLimits';

type Flow = ReturnType<typeof useHireHealthSurveyFlow>;

const YES_NO = (t: Flow['t']): { value: YesNo; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.hireHealthSurvey.yes') },
  { value: 'no', label: t('submitDocuments.hireHealthSurvey.no') },
];

const FIT_OPTS = (t: Flow['t']): { value: FitChoice; label: string }[] => [
  { value: 'fit', label: t('submitDocuments.hireHealthSurvey.fit') },
  { value: 'not_fit', label: t('submitDocuments.hireHealthSurvey.notFit') },
];

function parseIsoDate(s: string): WheelDateValue | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export default function HireHealthSurveyChatComposer({
  flow,
  wheelLocale,
}: {
  flow: Flow;
  wheelLocale: string;
}) {
  const { step, examDraft, textDraft, setExamDraft, setTextDraft, t, advance, handleGateYesNo, handleFit } =
    flow;

  const isGate = step.endsWith('_gate');
  const isDate = step.endsWith('_date');
  const isHospital = step.endsWith('_hospital');
  const isFit = step.endsWith('_fit');
  const isDetail = step.endsWith('_detail');

  useEffect(() => {
    if (!isDate) return;
    if (examDraft.examDate) return;
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
    setExamDraft((d) => ({ ...d, examDate: iso }));
  }, [isDate, examDraft.examDate, setExamDraft]);

  if (isGate) {
    const isSymptoms = step.startsWith('symptoms');
    const value = isSymptoms ? textDraft.hadIssue || null : examDraft.hadExam || null;
    const noLabel = isSymptoms ? t('submitDocuments.hireHealthSurvey.noSymptoms') : undefined;
    const opts = YES_NO(t).map((o) =>
      o.value === 'no' && noLabel ? { ...o, label: noLabel } : o
    );
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={value} options={opts} onChange={(v) => void handleGateYesNo(v)} />
      </div>
    );
  }

  if (isDate) {
    const parsed = parseIsoDate(examDraft.examDate);
    const wheelValue: WheelDateValue = parsed ?? {
      year: new Date().getFullYear(),
      month: 1,
      day: 1,
    };
    const handleSendDate = () => void advance();
    const sendAria = t('submitDocuments.profileName.send');
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker
            locale={wheelLocale}
            value={wheelValue}
            onChange={(v) => {
              const iso = `${v.year}-${String(v.month).padStart(2, '0')}-${String(v.day).padStart(2, '0')}`;
              setExamDraft((d) => ({ ...d, examDate: iso }));
            }}
          />
        </div>
        <div className="ff-text-input__footer">
          <button type="button" className="ff-text-input__send" onClick={handleSendDate} aria-label={sendAria}>
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (isHospital) {
    return (
      <TextInput
        fields={[
          {
            id: 'hospital',
            label: '',
            hideLabel: true,
            ariaLabel: t(stepQuestionKey(step)),
            required: true,
          },
        ]}
        values={{ hospital: examDraft.hospital }}
        onChange={(id, v) => setExamDraft((d) => ({ ...d, hospital: v }))}
        onSubmit={() => void advance()}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.workerRegistryChat.required')}
      />
    );
  }

  if (isFit) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={examDraft.fit || null}
          options={FIT_OPTS(t)}
          onChange={(v) => {
            if (v === 'fit' || v === 'not_fit') void handleFit(v);
          }}
        />
      </div>
    );
  }

  if (isDetail) {
    const sendAria = t('submitDocuments.profileName.send');
    const detailLen = textDraft.detail.length;
    const canSendDetail = textDraft.detail.trim().length > 0;
    const handleSendDetail = () => {
      if (!canSendDetail) return;
      void advance();
    };
    const charLimitHint = t('submitDocuments.hireHealthSurvey.detailCharLimit').replace(
      /\{\{max\}\}/g,
      String(HIRE_HEALTH_DETAIL_MAX_CHARS)
    );
    const charCount = t('submitDocuments.hireHealthSurvey.detailCharCount')
      .replace(/\{\{count\}\}/g, String(detailLen))
      .replace(/\{\{max\}\}/g, String(HIRE_HEALTH_DETAIL_MAX_CHARS));
    const twoBoxHint = t('submitDocuments.hireHealthSurvey.detailTwoBoxHint').replace(
      /\{\{lineMax\}\}/g,
      String(HIRE_HEALTH_TEXT_BOX_MAX_CHARS)
    );
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <textarea
            className="ff-text-input__input"
            rows={4}
            maxLength={HIRE_HEALTH_DETAIL_MAX_CHARS}
            value={textDraft.detail}
            onChange={(e) =>
              setTextDraft((d) => ({
                ...d,
                detail: e.target.value.slice(0, HIRE_HEALTH_DETAIL_MAX_CHARS),
              }))
            }
            style={{ width: '100%', resize: 'vertical' }}
            aria-label={t(stepQuestionKey(step))}
          />
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#666', lineHeight: 1.4 }}>
            {charLimitHint} {twoBoxHint}
          </p>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 12,
              color: detailLen >= HIRE_HEALTH_DETAIL_MAX_CHARS ? '#b83d4c' : '#888',
              textAlign: 'right',
            }}
            aria-live="polite"
          >
            {charCount}
          </p>
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={handleSendDetail}
            disabled={!canSendDetail}
            aria-label={sendAria}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
